import { dateRange, degree, escapeHtml, fullName, locationText, mdToHtml, type TailoredResume } from "./markdown";

export const meta = { id: "compact", name: "Compact" };

const CSS = `
  :root { --ink: #1c1c1c; --muted: #4d4d4d; --faint: #6f6f6f; --rule: #333; }
  * { box-sizing: border-box; }
  body { margin: 0; background: #ededf0; color: var(--ink);
    font-family: "Times New Roman", Times, Georgia, serif; }
  .page { max-width: 8.5in; margin: 22px auto; background: #fff; padding: 0.5in 0.6in;
    box-shadow: 0 1px 6px rgba(0,0,0,0.14); }
  header { border-bottom: 1.5px solid var(--rule); padding-bottom: 8px; margin-bottom: 10px;
    display: flex; justify-content: space-between; align-items: flex-end; gap: 24px; }
  .id { flex: 0 0 auto; }
  .id h1 { font-size: 20px; font-weight: 700; margin: 0; white-space: nowrap; }
  .id .title { font-size: 12px; color: var(--muted); margin-top: 2px; }
  .contact { flex: 1 1 auto; min-width: 0; font-size: 9.5px; color: var(--muted); line-height: 1.6;
    text-align: right; overflow-wrap: anywhere; }
  section { margin-top: 11px; }
  h2 { font-size: 15px; text-transform: uppercase; font-weight: 700;
    border-bottom: 1px solid var(--rule); padding-bottom: 2px; margin: 0 0 6px; }
  .body { font-size: 13px; line-height: 1.4; }
  .body p { margin: 0 0 4px; }
  .body ul { margin: 3px 0 5px; padding-left: 15px; }
  .body li { margin: 1.5px 0; }
  .entry { margin-bottom: 8px; }
  .entry:last-child { margin-bottom: 0; }
  .row { display: flex; justify-content: space-between; gap: 10px; align-items: baseline; }
  .role { font-weight: 700; font-size: 14px; }
  .org { color: var(--muted); font-style: italic; }
  .meta { color: var(--faint); font-size: 12px; white-space: nowrap; }
  .rel { font-size: 12px; color: var(--muted); margin-top: 1px; }
  .rel ul { display: inline; margin: 0; padding: 0; }
  .rel li { display: inline; }
  .rel li:not(:last-child)::after { content: ", "; }
  .inline { font-size: 13px; }
  .ref { font-size: 13px; margin-bottom: 5px; line-height: 1.4; }
  .ref .name { font-weight: 700; }
  @media print {
    body { background: #fff; }
    .page { box-shadow: none; margin: 0; max-width: none; padding: 0.35in 0.45in; }
  }
`;

const has = (v: unknown) => v != null && (Array.isArray(v) ? v.length > 0 : String(v).trim() !== "");
const section = (title: string, inner: string) =>
	has(inner) ? `<section><h2>${title}</h2><div class="body">${inner}</div></section>` : "";

function header(r: TailoredResume) {
	const c = (r.contact || {}) as TailoredResume["contact"];
	const rows = [[c.email, c.phone].filter(Boolean).map(escapeHtml).join("  |  "), locationText(c.location)]
		.filter(Boolean)
		.map((line) => `<div>${line}</div>`)
		.join("");
	return `<header>
      <div class="id"><h1>${escapeHtml(fullName(c))}</h1>${c.title ? `<div class="title">${escapeHtml(c.title)}</div>` : ""}</div>
      <div class="contact">${rows}</div>
    </header>`;
}

const experience = (list: any[] | undefined) =>
	(list || [])
		.map(
			(j) => `<div class="entry">
      <div class="row"><span class="role">${escapeHtml(j.jobTitle || "")}<span class="org"> — ${escapeHtml(j.employer || "")}</span></span><span class="meta">${escapeHtml(dateRange(j))}${locationText(j.location) ? " · " + escapeHtml(locationText(j.location)) : ""}</span></div>
      ${has(j.highlights) ? mdToHtml(j.highlights) : ""}
      ${has(j.relevantSkills) ? `<div class="rel">Skills: ${mdToHtml(j.relevantSkills)}</div>` : ""}
    </div>`,
		)
		.join("");

const education = (list: any[] | undefined) =>
	(list || [])
		.map(
			(e) => `<div class="entry">
      <div class="row"><span class="role">${escapeHtml(degree(e))}<span class="org"> — ${escapeHtml(e.schoolName || "")}</span></span><span class="meta">${escapeHtml(e.graduation || "")}</span></div>
      ${has(e.details) ? mdToHtml(e.details) : ""}
    </div>`,
		)
		.join("");

const languages = (list: any[] | undefined) =>
	`<div class="inline">${(list || []).map((l) => `${escapeHtml(l.language)}${l.proficiency ? ` (${escapeHtml(l.proficiency)})` : ""}`).join("  |  ")}</div>`;

const references = (list: any[] | undefined) =>
	(list || [])
		.map(
			(r) =>
				`<div class="ref"><span class="name">${escapeHtml(r.name || "")}</span>${r.designation ? `, ${escapeHtml(r.designation)}` : ""}${r.workPlace ? ` — ${escapeHtml(r.workPlace)}` : ""} · ${[r.phone, r.email].filter(Boolean).map(escapeHtml).join(" · ")}</div>`,
		)
		.join("");

const customSections = (list: any[] | undefined) =>
	(list || []).map((s) => section(escapeHtml(s.title), has(s.content) ? mdToHtml(s.content) : "")).join("");

const linksList = (list: any[] | undefined) =>
	(list || []).map((l) => `<div>${l.type ? escapeHtml(l.type) + ": " : ""}${escapeHtml(l.value)}</div>`).join("");

export function renderBody(r: TailoredResume): string {
	return [
		header(r),
		section("Summary", has(r.summary) ? mdToHtml(r.summary) : ""),
		section("Skills", has(r.skills) ? mdToHtml(r.skills) : ""),
		section("Experience", experience(r.workExperience)),
		section("Education", education(r.education)),
		section("Certifications", has(r.certifications) ? mdToHtml(r.certifications) : ""),
		customSections(r.customSections),
		section("Accomplishments", has(r.accomplishments) ? mdToHtml(r.accomplishments) : ""),
		section("Affiliations", has(r.affiliations) ? mdToHtml(r.affiliations) : ""),
		section("Languages", has(r.languages) ? languages(r.languages) : ""),
		section("Additional Information", has(r.additionalInfo) ? mdToHtml(r.additionalInfo) : ""),
		section("Links", has(r.personalLinks) ? linksList(r.personalLinks) : ""),
		section("References", references(r.references)),
	].join("");
}

export function renderDocument(resume: TailoredResume): string {
	return `<!doctype html><html><head><meta charset="utf-8" /><style>${CSS}</style></head><body><div class="page" id="resume">${renderBody(resume)}</div></body></html>`;
}
